import { toPersianDigits } from './digits.js';

/**
 * Jalali (Solar Hijri) <-> Gregorian conversion (BR-03).
 * Storage is always Gregorian/UTC; Jalali is presentation only.
 * Algorithm: Borkowski's leap-year breaks (the same one used by jalaali-js),
 * verified against the legacy app's `g2j`/`j2g` as a test oracle.
 */

export interface JalaliDate {
  jy: number;
  jm: number;
  jd: number;
}

export interface GregorianDate {
  gy: number;
  gm: number;
  gd: number;
}

const BREAKS = [
  -61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178,
] as const;

const div = (a: number, b: number): number => Math.trunc(a / b);
const mod = (a: number, b: number): number => a - Math.trunc(a / b) * b;

interface JalCal {
  leap: number;
  gy: number;
  march: number;
}

function jalCal(jy: number): JalCal {
  const first = BREAKS[0];
  const last = BREAKS[BREAKS.length - 1] as number;
  if (jy < first || jy >= last) {
    throw new RangeError(`Jalali year out of supported range: ${jy}`);
  }
  const gy = jy + 621;
  let leapJ = -14;
  let jp: number = first;
  let jump = 0;
  for (let i = 1; i < BREAKS.length; i += 1) {
    const jm = BREAKS[i] as number;
    jump = jm - jp;
    if (jy < jm) break;
    leapJ = leapJ + div(jump, 33) * 8 + div(mod(jump, 33), 4);
    jp = jm;
  }
  let n = jy - jp;
  leapJ = leapJ + div(n, 33) * 8 + div(mod(n, 33) + 3, 4);
  if (mod(jump, 33) === 4 && jump - n === 4) leapJ += 1;
  const leapG = div(gy, 4) - div((div(gy, 100) + 1) * 3, 4) - 150;
  const march = 20 + leapJ - leapG;
  if (jump - n < 6) n = n - jump + div(jump + 4, 33) * 33;
  let leap = mod(mod(n + 1, 33) - 1, 4);
  if (leap === -1) leap = 4;
  return { leap, gy, march };
}

function g2d(gy: number, gm: number, gd: number): number {
  let d = div((gy + div(gm - 8, 6) + 100100) * 1461, 4) + div(153 * mod(gm + 9, 12) + 2, 5) + gd - 34840408;
  d = d - div(div(gy + 100100 + div(gm - 8, 6), 100) * 3, 4) + 752;
  return d;
}

function d2g(jdn: number): GregorianDate {
  let j = 4 * jdn + 139361631;
  j = j + div(div(4 * jdn + 183187720, 146097) * 3, 4) * 4 - 3908;
  const i = div(mod(j, 1461), 4) * 5 + 308;
  const gd = div(mod(i, 153), 5) + 1;
  const gm = mod(div(i, 153), 12) + 1;
  const gy = div(j, 1461) - 100100 + div(8 - gm, 6);
  return { gy, gm, gd };
}

function j2d(jy: number, jm: number, jd: number): number {
  const r = jalCal(jy);
  return g2d(r.gy, 3, r.march) + (jm - 1) * 31 - div(jm, 7) * (jm - 7) + jd - 1;
}

function d2j(jdn: number): JalaliDate {
  const gy = d2g(jdn).gy;
  let jy = gy - 621;
  const r = jalCal(jy);
  const jdn1f = g2d(gy, 3, r.march);
  let k = jdn - jdn1f;
  if (k >= 0) {
    if (k <= 185) {
      return { jy, jm: 1 + div(k, 31), jd: mod(k, 31) + 1 };
    }
    k -= 186;
  } else {
    jy -= 1;
    k += 179;
    if (r.leap === 1) k += 1;
  }
  return { jy, jm: 7 + div(k, 30), jd: mod(k, 30) + 1 };
}

export function isJalaliLeapYear(jy: number): boolean {
  return jalCal(jy).leap === 0;
}

export function jalaliMonthLength(jy: number, jm: number): number {
  if (jm <= 6) return 31;
  if (jm <= 11) return 30;
  return isJalaliLeapYear(jy) ? 30 : 29;
}

export function isValidJalaliDate(jy: number, jm: number, jd: number): boolean {
  return (
    Number.isInteger(jy) &&
    Number.isInteger(jm) &&
    Number.isInteger(jd) &&
    jy >= -61 &&
    jy < 3177 &&
    jm >= 1 &&
    jm <= 12 &&
    jd >= 1 &&
    jd <= jalaliMonthLength(jy, jm)
  );
}

export function gregorianToJalali(gy: number, gm: number, gd: number): JalaliDate {
  return d2j(g2d(gy, gm, gd));
}

export function jalaliToGregorian(jy: number, jm: number, jd: number): GregorianDate {
  if (!isValidJalaliDate(jy, jm, jd)) {
    throw new RangeError(`Invalid Jalali date: ${jy}/${jm}/${jd}`);
  }
  return d2g(j2d(jy, jm, jd));
}

export const DISPLAY_TIME_ZONE = 'Asia/Tehran';

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const wallClockFormatters = new Map<string, Intl.DateTimeFormat>();

/** Wall-clock parts of an instant in a time zone (default: Asia/Tehran). */
export function wallClock(instant: Date, timeZone: string = DISPLAY_TIME_ZONE): WallClock {
  let fmt = wallClockFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    wallClockFormatters.set(timeZone, fmt);
  }
  const parts: Record<string, string> = {};
  for (const p of fmt.formatToParts(instant)) parts[p.type] = p.value;
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
  };
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** An instant (UTC) shown as a Jalali date in Tehran time, e.g. `۱۴۰۵/۰۷/۰۷`. */
export function formatJalaliDate(instant: Date | string, timeZone?: string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const w = wallClock(d, timeZone);
  const j = gregorianToJalali(w.year, w.month, w.day);
  return toPersianDigits(`${j.jy}/${pad2(j.jm)}/${pad2(j.jd)}`);
}

/** An instant (UTC) shown as Jalali date and time in Tehran, e.g. `۱۴۰۵/۰۷/۰۷ ۱۴:۰۵`. */
export function formatJalaliDateTime(instant: Date | string, timeZone?: string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const w = wallClock(d, timeZone);
  const j = gregorianToJalali(w.year, w.month, w.day);
  return toPersianDigits(`${j.jy}/${pad2(j.jm)}/${pad2(j.jd)} ${pad2(w.hour)}:${pad2(w.minute)}`);
}

export const JALALI_MONTH_NAMES = [
  'فروردین',
  'اردیبهشت',
  'خرداد',
  'تیر',
  'مرداد',
  'شهریور',
  'مهر',
  'آبان',
  'آذر',
  'دی',
  'بهمن',
  'اسفند',
] as const;

/** Day and month name in Tehran time, e.g. `۸ مهر` (the legacy SMS `{تاریخ}` placeholder). */
export function formatJalaliDayMonth(instant: Date | string, timeZone?: string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const w = wallClock(d, timeZone);
  const j = gregorianToJalali(w.year, w.month, w.day);
  return `${toPersianDigits(j.jd)} ${JALALI_MONTH_NAMES[j.jm - 1] as string}`;
}

/** Clock time in Tehran, hour not padded, e.g. `۹:۰۰` or `۱۰:۳۰` (the legacy `{ساعت}`). */
export function formatClockTime(instant: Date | string, timeZone?: string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const w = wallClock(d, timeZone);
  return toPersianDigits(`${w.hour}:${pad2(w.minute)}`);
}

/** A date-only value (`YYYY-MM-DD`, Gregorian) shown as Jalali, e.g. expiry dates. */
export function formatJalaliFromIsoDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) throw new RangeError(`Invalid ISO date: ${isoDate}`);
  const j = gregorianToJalali(Number(match[1]), Number(match[2]), Number(match[3]));
  return toPersianDigits(`${j.jy}/${pad2(j.jm)}/${pad2(j.jd)}`);
}
